import { Button, Flex, Stack, Text, useMantineTheme } from "@mantine/core";
import { darkNotificationProps } from "@common/constants/theme-mantine/styling";
import IconCheckRounded from "@common/components/icons/IconCheckRounded";
import ErrorMessage from "@common/components/text/ErrorMessage";
import CustomCard from "@common/components/thirdParty/mantine/card/CustomCard";
import { ICustomCardProps } from "@common/components/thirdParty/mantine/card/CustomCard/types";
import { formatPrice } from "@common/utils/string";
import useChangePlanWithInstanceRestart from "@hooks/useChangePlanWithInstanceRestart";
import { showSuccessNotification } from "@utils/notifications";
import { IAddOnSlotProps, IMeteredAddOnDescriptor } from "../../types";

export function TransformsAdvancedMeteredUpsell({
  addOn: {
    purchase,
    addOn,
    getPricePerUnit,
    getIncludedUnits,
    getEnableAddOnPayload,
  },
  ...cardProps
}: IAddOnSlotProps<IMeteredAddOnDescriptor> &
  Omit<ICustomCardProps, "children">) {
  const theme = useMantineTheme();

  const {
    isPending: loadingChangePlan,
    isUpdatingSubscription,
    error: changePlanError,
    mutate: changePlan,
  } = useChangePlanWithInstanceRestart(purchase);

  // nothing to quote or enable without the catalogue row
  if (!addOn) {
    return null;
  }

  const includedUnits = getIncludedUnits();

  const updateTransforms = () =>
    changePlan(getEnableAddOnPayload(), {
      onSuccess() {
        showSuccessNotification({
          ...darkNotificationProps,
          icon: <IconCheckRounded />,
          message: "Transforms successfully updated",
        });
      },
    });

  return (
    <CustomCard
      data-testid="TransformsAdvancedMeteredUpsell"
      bg="gray.1"
      withBorder
      title={
        <>
          Advanced transforms
          <Text span c="gray.5">
            <Text span fz="xs" px="xs" top={-2} pos="relative">
              •
            </Text>
            {formatPrice(getPricePerUnit())} per transform
          </Text>
          <ErrorMessage error={changePlanError} />
        </>
      }
      extra={
        <Button
          h="2rem"
          px="sm"
          loading={isUpdatingSubscription}
          disabled={loadingChangePlan}
          onClick={updateTransforms}
          mb="-xs"
        >
          Upgrade
        </Button>
      }
      {...cardProps}
    >
      <Stack spacing="md">
        <Text fz="sm">
          Unlock additional features for professional transforms use.
        </Text>
        <Flex
          align="flex-start"
          direction={{ base: "column", sm: "row" }}
          gap={{ base: "xs", sm: "xl" }}
          justify="flex-start"
          wrap="nowrap"
          w="100%"
        >
          {[
            "Separate DB write connections",
            "Transform inspection",
            "Python runners",
            includedUnits > 0
              ? `${includedUnits.toLocaleString()} runs included`
              : null,
          ].map((featureStr, i) => (
            <Flex key={i} wrap="nowrap">
              <Flex style={{ flexShrink: 0 }}>
                <IconCheckRounded color={theme.colors.brand[4]} size={14} />
              </Flex>
              <Text fz="xs" ml="xs" c="gray.6" lh={1.2}>
                {featureStr}
              </Text>
            </Flex>
          ))}
        </Flex>
      </Stack>
    </CustomCard>
  );
}
